import { getSubscriptionOfUser } from "../../redux/actions/subscriptions.dispatcher";
import {
  getAllCreators,
  getCreatorsSubscribed,
  getUserById,
  subscribeCreator,
} from "../../redux/actions/user.dispatcher";
import Layout from "../../layouts/Layout";
import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ErrorSnackbar } from "../../Components/Common/CustomSnackbar";
import { SecondaryBtn } from "../../Components/Common/CustomBtn";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  getAllPosts,
  subscribeToContent,
} from "../../redux/actions/posts.dispatcher";
import { colors } from "../../theme/theme";
import {
  ArrowBack,
  Close,
  East,
  EmailOutlined,
  PersonOutline,
  Today,
} from "@mui/icons-material";
import {
  CardElement,
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import axiosInstance from "../../utils/config/config";

const Subscription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { type } = useParams();
  const creator =
    type === "video"
      ? location.state.paidVideoData?.creator
      : location.state.creatorData;
  const packageDetails = location.state.packageDetails || null;
  const { userData } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const [success, setSuccess] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });
  const [selectedSubscription, setSelectedSubscription] = useState(
    creator?.subscriptions?.[0] || null
  );

  const handlePackageSelect = (pkg) => {
    setSelectedSubscription(pkg);
  };

  const fetchSavedCards = async () => {
    try {
      const { data } = await axiosInstance.get(`/payments/getUserInfo`);
      const cards = data.paymentMethods || [];
      setSavedCards(cards);
      setAddingNew(cards.length === 0);
    } catch (err) {
      setErrorState({ error: true, message: "Failed to fetch cards." });
    }
  };

  useEffect(() => {
    fetchSavedCards();
  }, []);

  const stripeStyle = {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": {
        color: "#999",
      },
    },
    invalid: {
      color: "#e5424d",
    },
  };

  const handleSubscription = async () => {
    setErrorState({ error: false, message: "" });
    if (!stripe || !elements) return;

    try {
      setLoading(true);
      let paymentMethodId = selectedCardId;

      if (addingNew) {
        const { data: setupIntentRes } = await axiosInstance.post(
          "/payments/createPaymentIntent"
        );

        const result = await stripe.confirmCardSetup(
          setupIntentRes.clientSecret,
          {
            payment_method: {
              card: elements.getElement(CardNumberElement),
            },
          }
        );

        if (result.error) {
          setErrorState({ error: true, message: result.error.message });
          return;
        }

        paymentMethodId = result.setupIntent.payment_method;
      }

      if (type === "creator") {
        await subscribeCreator(
          creator.creatorId,
          selectedSubscription.id,
          paymentMethodId // from setupIntent
        );

        dispatch(getCreatorsSubscribed());
        dispatch(getAllCreators());
        dispatch(getUserById(userData.id));
        navigate(`/profile/${creator.id}`);
      } else {
        const response = await subscribeToContent(
          location.state.paidVideoData?.id,
          paymentMethodId
        );

        if (response) {
          setSuccess(true);
          dispatch(getAllPosts());
          navigate(`/profile/${creator.id}`);
        }
      }
    } catch (error) {
      setErrorState({ error: true, message: error.message });
    } finally {
      setLoading(false);
      if (location.state.setup) {
        navigate("/onboarding", {
          state: {
            followerReturn: true,
          },
        });
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ pb: 12 }}>
        <Backdrop open={loading} sx={{ zIndex: 9999 }}>
          <CircularProgress />
        </Backdrop>

        <ErrorSnackbar
          error={errorState.error}
          errorMsg={errorState.message}
          handleCloseError={() => setErrorState({ error: false, message: "" })}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: 2,
            py: 2,
          }}
        >
          <IconButton onClick={() => navigate(-1)}>
            {" "}
            <ArrowBack sx={{ color: "#000" }} />{" "}
          </IconButton>
          <Typography
            sx={{
              fontSize: "23px",
              fontFamily: "Inter-SemiBold",
              color: colors.textColor,
            }}
          >
            {type === "video" ? "Video Subscription" : "Creator Subscription"}
          </Typography>
        </Box>

        <Box sx={{ m: 3 }}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 4,
            }}
          >
            <img
              src={creator?.avatar || require("../../assets/images/user.jpg")}
              style={{
                height: "100px",
                width: "150px",
                objectFit: "cover",
                // border: "1px solid #000",
              }}
              alt="avatar"
            />

            <Stack direction="column">
              <Typography
                sx={{
                  fontFamily: "Inter-SemiBold",
                  fontSize: "24px",
                  fontWeight: "600",
                }}
              >
                {creator?.firstName + " " + creator?.lastName}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: "#00000080",
                  fontSize: "18px",
                  fontFamily: "Inter-Regular",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 5,
                  textOverflow: "ellipsis",
                }}
              >
                {creator?.bio}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter-Medium",
                  color: colors.textColor,
                  fontWeight: "400",
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  mt: 3,
                }}
              >
                Pay
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Inter-SemiBold",
                    color: colors.primary,
                    textTransform: "uppercase",
                    letterSpacing: 2,
                  }}
                  onClick={() => {
                    console.log(
                      "location.state.paidVideoData",
                      location.state.paidVideoData
                    );
                  }}
                >
                  {type === "video"
                    ? ` $${(location.state.paidVideoData.discount
                        ? location.state.paidVideoData.price *
                          (1 - location.state.paidVideoData.discount / 100)
                        : location.state.paidVideoData.price
                      ).toFixed(2)}`
                    : ` $${selectedSubscription?.price}`}
                </Typography>
                {type === "video"
                  ? "to unlock this video only"
                  : `to subscribe to this creator's `}
                {type !== "video" && selectedSubscription?.packageName && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Inter-SemiBold",
                      color: colors.primary,
                      textTransform: "uppercase",
                      letterSpacing: 2,
                    }}
                  >
                    {selectedSubscription.packageName}
                    <Typography
                      sx={{
                        fontFamily: "Inter-Medium",
                        color: colors.textColor,
                        fontWeight: "400",
                        textTransform: "uppercase",
                        letterSpacing: 2,
                        mt: 3,
                      }}
                    >
                      {" "}
                      Plan
                    </Typography>
                  </Typography>
                )}
              </Typography>
            </Stack>
          </Stack>

          {creator?.subscriptions?.length > 0 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
              sx={{ my: 3 }}
            >
              {creator.subscriptions.map((pkg, idx) => {
                const isSelected =
                  selectedSubscription?.packageName === pkg.packageName;
                return (
                  <Box
                    key={idx}
                    onClick={() => handlePackageSelect(pkg)}
                    sx={{
                      border: isSelected
                        ? "2px solid #FC0108"
                        : "1px solid #ccc",
                      borderRadius: 2,
                      p: 2,
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#FFF5F5" : "#fff",
                      width: { xs: "100%", sm: "230px" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {pkg.packageName}
                    </Typography>
                    <Typography sx={{ color: colors.primary, mt: 0.5 }}>
                      ${pkg.price}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#666", mt: 1 }}>
                      {pkg.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}

          <Typography sx={{ fontSize: "24px", fontWeight: 600 }}>
            Payment Method
          </Typography>
          {!addingNew && savedCards.length > 0 && (
            <Stack spacing={2} mb={3}>
              {savedCards.map((card) => (
                <Box
                  key={card.id}
                  onClick={() => setSelectedCardId(card.id)}
                  sx={{
                    border:
                      selectedCardId === card.id
                        ? "2px solid #FC0108"
                        : "1px solid #ccc",
                    borderRadius: 2,
                    p: 2,
                    cursor: "pointer",
                    backgroundColor:
                      selectedCardId === card.id ? "#FFF5F5" : "#fff",
                  }}
                >
                  <Typography fontWeight="bold">
                    **** **** **** {card.card.last4}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {card.card.brand} — Exp {card.card.exp_month}/
                    {card.card.exp_year}
                  </Typography>
                </Box>
              ))}
              <SecondaryBtn
                text="Add New Card"
                width="200px"
                noIcon
                onClick={() => {
                  setAddingNew(true);
                  setSelectedCardId(null);
                }}
              />
            </Stack>
          )}

          {addingNew && (
            <Box mt={3}>
              <Typography variant="body2" mb={1}>
                Enter your card info
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxWidth: 500,
                }}
              >
                <Box sx={{ border: "1px solid #ccc", p: 2, borderRadius: 2 }}>
                  <Typography variant="caption">Card Number</Typography>
                  <CardNumberElement options={{ style: stripeStyle }} />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      p: 2,
                      borderRadius: 2,
                      flex: 1,
                    }}
                  >
                    <Typography variant="caption">Expiry</Typography>
                    <CardExpiryElement options={{ style: stripeStyle }} />
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      p: 2,
                      borderRadius: 2,
                      flex: 1,
                    }}
                  >
                    <Typography variant="caption">CVC</Typography>
                    <CardCvcElement options={{ style: stripeStyle }} />
                  </Box>
                </Box>
              </Box>

              {savedCards.length > 0 && (
                <SecondaryBtn
                  text="Back to Saved Cards"
                  width="200px"
                  noIcon
                  onClick={() => {
                    setAddingNew(false);
                    setSelectedCardId(null);
                  }}
                />
              )}
            </Box>
          )}

          <Stack direction="row" spacing={2} mt={4}>
            <SecondaryBtn
              text="Cancel"
              width="50%"
              onClick={() => navigate("/dashboard")}
              noIcon
            />
            <SecondaryBtn
              text="Subscribe"
              width="50%"
              onClick={handleSubscription}
              filled
              disabled={!stripe || (!selectedCardId && !addingNew)}
              noIcon
            />
          </Stack>
        </Box>
      </Box>
    </Layout>
  );
};

export default Subscription;
